import PropTypes from 'prop-types';
import CorgixInputField from '@veeva/corgix/input-field';
import { css } from '@emotion/core';
import { isMobile } from '../services/DeviceInformation';
import mobileContentWidthCSS from '../constants/mobileContentWidthCSS';
import labelLineHeightCSS from '../constants/labelLineHeightCSS';

const propTypes = {
    /**
     * Automation marker
     */
    [`data-target`]: PropTypes.string,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.node,
    id: PropTypes.string,
    label: PropTypes.string,
    /**
     * Callback fired when input value is changed. Returns only the value.
     *
     * `onChange(value)`
     */
    onChange: PropTypes.func,
    onKeyUp: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    /**
     * Icons within the input. Must return an array for multiple icons or one icon.
     * Other component types are not supported (i.e. fragments, divs, spans, etc.)
     */
    renderIcons: PropTypes.func,
    type: PropTypes.oneOf(['password', 'text']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const InputField = ({
    ariaLabel,
    className,
    error,
    errorMessage,
    disabled,
    id,
    label,
    onChange,
    onKeyUp,
    placeholder,
    readOnly = false,
    renderIcons,
    type = 'text',
    value,
    [`data-target`]: dataTarget,
}) => {
    const inputCSS = ({ veevaIdErrorLabelBufferHeight, lightGray }) => {
        return [
            css`
                input {
                    color: ${readOnly ? lightGray : ''};
                    width: 100%;
                }

                /* Error label buffer */
                > div > div:nth-of-type(2) {
                    padding-top: 0.333rem;
                    padding-bottom: 0;
                    height: ${veevaIdErrorLabelBufferHeight};
                    min-height: fit-content;
                    max-height: none;
                    text-align: left;
                }
                width: 100%;

                label {
                    padding: 0;
                    margin-bottom: 0.667rem;
                    ${labelLineHeightCSS}
                }
            `,
            isMobile() &&
                css`
                    /* Input wrapper container */
                    & > div > div {
                        justify-content: center;

                        /* Input wrapper */
                        & > div {
                            ${mobileContentWidthCSS};
                        }
                    }
                `,
        ];
    };

    return (
        <CorgixInputField
            className={className}
            css={inputCSS}
            error={error || !!errorMessage}
            errorMessage={errorMessage}
            disabled={disabled}
            id={id}
            label={label}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyUp={onKeyUp}
            placeholder={placeholder}
            readOnly={readOnly}
            size="xl"
            type={type}
            value={value}
            aria-label={ariaLabel}
            data-target={dataTarget}
        >
            {renderIcons?.()}
        </CorgixInputField>
    );
};

InputField.propTypes = propTypes;

export default InputField;
